import { useId } from 'react'
import type { InputHTMLAttributes, JSX, ReactNode } from 'react'

/**
 * A labelled control with help and error text, wired for assistive technology.
 *
 * The wiring is the reason this exists rather than each form repeating the
 * markup: `aria-describedby` has to name the help text *and* the error, and
 * `aria-invalid` has to follow the error's presence. Getting that right six
 * times by hand is six chances to get it wrong once, invisibly.
 *
 * Validity is announced by border, icon and message together, never by colour
 * alone — the `data-state` attribute the Atlas layer keys off (S-D1).
 *
 * **`error` is a node, not a string** (AUTH-2). S-D2 screen 02 puts two
 * recovery links inside the message under the email field — *"An account
 * already uses this email. Sign in instead or reset your password."* — and a
 * refusal that tells somebody what went wrong without offering the way out of
 * it is the half of the design that does not help anyone. (The mockup writes
 * *Log in*; the product says *Sign in*, on the owner's decision of 2026-09-06.)
 */
export function Field({
  label,
  error,
  ok,
  invalid,
  help,
  required,
  mark = false,
  children,
}: {
  /** A string, or a node when the label carries markup — `Type <code>Kavya Interiors</code> to confirm`. */
  label: ReactNode
  error?: ReactNode
  /**
   * Valid, and worth saying so — *Available* under the workspace address once
   * the live check has answered (spec: *Signup*). The design's `.ar-field__ok`
   * line with its `✓`, and `data-state="valid"` on the field so the border
   * agrees. Ignored while `error` or `invalid` is set: a field cannot be both,
   * and the refusal is the one that stops the submit.
   */
  ok?: ReactNode
  /**
   * Invalid, with nothing to say about it (AUTH-6).
   *
   * S-D2 screen 04 marks *both* login fields `data-state="invalid"` and
   * `aria-invalid="true"` and puts no message under either, because the only
   * honest message would name which half was wrong — and the whole point of
   * that screen's generic refusal is that it does not. So the two states are
   * separate props: `error` is "invalid, and here is why", this is "invalid,
   * and the summary already said why".
   */
  invalid?: boolean | undefined
  help?: ReactNode
  required?: boolean | undefined
  /** Draw the red `*` after the label — only where the mockup draws one. */
  mark?: boolean | undefined
  /** Receives the id and the aria wiring the label and messages depend on. */
  children: (props: {
    id: string
    'aria-describedby': string | undefined
    'aria-invalid': boolean | undefined
    required: boolean | undefined
  }) => ReactNode
}): JSX.Element {
  const id = useId()
  const helpId = `${id}-help`
  const errorId = `${id}-error`
  const okId = `${id}-ok`

  const wrong = Boolean(error) || Boolean(invalid)
  const right = !wrong && Boolean(ok)
  const describedBy = [help ? helpId : null, error ? errorId : null, right ? okId : null]
    .filter(Boolean)
    .join(' ')

  return (
    <div className="ar-field" data-state={wrong ? 'invalid' : right ? 'valid' : undefined}>
      {/* No asterisk on a required field unless the screen asks for one
          (parity, 2026-09-13): the mockups draw their labels bare — *Business
          name*, *Email address*, *From name* — with exactly two exceptions,
          the wizard's *Website* and the staff console's *Reason*, which carry
          `ar-req`. `required` still reaches the control below either way, so
          the browser and a screen reader know; the eye is told when the field
          is submitted empty, by the message this component renders. */}
      <label className="ar-field__label" htmlFor={id}>
        {label}
        {mark ? (
          <span className="ar-req" aria-hidden="true">
            *
          </span>
        ) : null}
      </label>
      {children({
        id,
        'aria-describedby': describedBy || undefined,
        'aria-invalid': wrong ? true : undefined,
        required,
      })}
      {help ? (
        <span className="ar-field__help" id={helpId}>
          {help}
        </span>
      ) : null}
      {error ? (
        <span className="ar-field__error" id={errorId}>
          {/*
            AUTH-36. `✕` is the design's glyph for a refusal — every field error
            and every danger alert in `design/screens/auth.html` carries it
            (`:130`, `:141`, `:199`, `:252`, `:343`), and `!` is reserved for
            the two states that are *not* a refusal of what was typed: the
            warning callout (`⚠`) and the error state that stands in for a
            whole screen (`auth.html:443`, still `!` here and in `states.tsx`).

            The app had `!` on all three, which is consistent but says the same
            thing about three different situations. Changed here rather than per
            screen because the glyph was never a screen's decision.
          */}
          <span aria-hidden="true">✕</span>
          {error}
        </span>
      ) : null}
      {right ? (
        <span className="ar-field__ok" id={okId}>
          <span aria-hidden="true">✓</span>
          {ok}
        </span>
      ) : null}
    </div>
  )
}

/** The common case: a `Field` wrapping one `<input>`. */
export function TextField({
  label,
  error,
  ok,
  invalid,
  help,
  mark,
  ...input
}: {
  label: string
  error?: ReactNode
  ok?: ReactNode
  invalid?: boolean | undefined
  help?: ReactNode
  mark?: boolean | undefined
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'id'>): JSX.Element {
  return (
    <Field
      label={label}
      error={error}
      ok={ok}
      invalid={invalid}
      help={help}
      required={input.required}
      mark={mark}
    >
      {(aria) => <input className="ar-input" {...aria} {...input} />}
    </Field>
  )
}
